import { useMemo } from "react";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { HistoryRounded as HistoryIcon } from "@mui/icons-material";
import { useRecordContext } from "react-admin";
import { ResourceName, type DocumentRead, type TargetSchema } from "@/types";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { featureFlags } from "@/utils/featureFlags";
import { claimItemsByReasonPath } from "@/resources/stoploss-claims/claimItems/ineligibleReasons";
import { TabContent } from "@/components/common/PageLayout";
import { useDocumentLoads } from "./hooks";
import { ResultStatusBanner } from "./ResultStatusBanner";
import { LoadWarningNotice } from "./LoadWarningNotice";
import { HELD_REASON_TEXT, parseLoadWarnings } from "./parseLoadWarnings";
import { buildAttentionMap } from "./loadResultModel";
import { SchemaResultCard, groupLoadedRecordsBySchema } from "./SchemaResultCard";
import { LoadResultView } from "./LoadResultView";
import { usesLoadResultView } from "../../utils/documentShowLayout";

/** Builds the DOM id for a schema's anchor card. Shared by the status banner's
 *  "Jump to" links and the card's id attribute. */
const schemaAnchorId = (schema: TargetSchema): string => `result-schema-${schema}`;

interface DocumentResultTabProps {
  /** True when the previous-loads side drawer is open. Driven by
   *  `DocumentShowContent` so the page-level width math can account for it. */
  previousLoadsOpen?: boolean;
  /** Callback invoked when the "Previous loads" trigger is clicked. Toggles
   *  the parent-owned drawer. Optional so the tab still renders standalone in
   *  isolation (e.g. tests). */
  onTogglePreviousLoads?: () => void;
}

/**
 * Result tab body.
 *
 * Renders, in order:
 *   - Top status banner with success/failure summary and per-schema anchor
 *     links for fast navigation.
 *   - Load warnings (#7213), e.g. stop-loss spend held behind a frozen claim.
 *   - "Previous loads" trigger button when prior loads exist (the drawer
 *     itself is owned by `DocumentShowContent` so it can stack alongside the
 *     always-on details drawer with proper width reservation).
 *   - One `SchemaResultCard` per target schema in the latest load. Each card
 *     uses `LoadedRecordsList` (replacing the legacy chunky datagrid) and a
 *     `FailedRecordsAccordion` for any per-schema failures.
 */
export const DocumentResultTab = ({ previousLoadsOpen, onTogglePreviousLoads }: DocumentResultTabProps = {}) => {
  const document = useRecordContext<DocumentRead>();
  const { latestLoad, previousLoadsCount, isLoading } = useDocumentLoads(document?.id);

  const groupedEntities = useMemo(
    () => groupLoadedRecordsBySchema(latestLoad?.loaded_records ?? []),
    [latestLoad?.loaded_records]
  );
  // Load warnings land on the rows they are about (held spend, skipped totals)
  // plus one quiet line — the same pattern the claims Result view uses.
  const parsedWarnings = useMemo(() => parseLoadWarnings(latestLoad?.normalization_warnings), [latestLoad]);
  const attention = useMemo(() => buildAttentionMap(parsedWarnings, HELD_REASON_TEXT), [parsedWarnings]);
  const { canList: canListClaimLines } = useResourcePermissions(ResourceName.CLAIM_ITEMS);

  if (!document) {
    return (
      <TabContent>
        <Typography>No document found</Typography>
      </TabContent>
    );
  }

  if (isLoading) {
    return (
      <TabContent>
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      </TabContent>
    );
  }

  if (!latestLoad) {
    return (
      <TabContent>
        <Box sx={{ textAlign: "center", py: 8, color: "text.secondary" }}>
          <Typography>No load results yet</Typography>
        </Box>
      </TabContent>
    );
  }

  // Single-client claims documents get the post-load view (#7550): context
  // bar, exception-only status, the claimants table. Everything else keeps
  // the per-schema cards below.
  if (usesLoadResultView(document)) {
    return (
      <TabContent>
        <LoadResultView
          documentRecord={document}
          latestLoad={latestLoad}
          previousLoadsCount={previousLoadsCount}
          previousLoadsOpen={previousLoadsOpen}
          onTogglePreviousLoads={onTogglePreviousLoads}
        />
      </TabContent>
    );
  }

  const loadedCount = latestLoad.loaded_records?.length ?? 0;
  const failedCount = latestLoad.failed_records?.length ?? 0;

  return (
    <TabContent>
      <ResultStatusBanner
        status={latestLoad.status}
        loadedCount={loadedCount}
        failedCount={failedCount}
        schemas={groupedEntities.map((g) => g.targetSchema)}
        schemaAnchorId={schemaAnchorId}
      />
      <Box sx={{ mb: 2, "&:empty": { display: "none" } }}>
        <LoadWarningNotice
          parsed={parsedWarnings}
          reasonFacetAvailable={featureFlags.deductibleBasisViewEnabled}
          noPaidDateHref={
            canListClaimLines && featureFlags.deductibleBasisViewEnabled
              ? claimItemsByReasonPath(
                  ["missing_paid_date"],
                  document.client_id_ref ? { client_id_ref: document.client_id_ref } : {}
                )
              : null
          }
        />
      </Box>

      {previousLoadsCount > 0 && onTogglePreviousLoads && (
        <Box sx={{ mb: 2 }}>
          <Button
            startIcon={<HistoryIcon fontSize="small" />}
            onClick={onTogglePreviousLoads}
            size="small"
            variant="text"
            sx={{ textTransform: "none" }}
          >
            {previousLoadsOpen ? "Hide" : "Show"} previous loads ({previousLoadsCount})
          </Button>
        </Box>
      )}

      <Stack spacing={2}>
        {groupedEntities.map((group) => (
          <Box key={group.targetSchema} id={schemaAnchorId(group.targetSchema)} sx={{ scrollMarginTop: 24 }}>
            <SchemaResultCard group={group} failedRecords={latestLoad.failed_records} attention={attention} />
          </Box>
        ))}
      </Stack>
    </TabContent>
  );
};
